import * as _ from 'underscore';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { appStore } from '../../app/AppStore';
import { IPlaceScope } from '../../app/IPlaceScope';
import { getProjectInfos } from '../../app/sagas/spaces/SpacesSagaHelpers';
import { CompositeSessionInfoSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ISiteSettingsForServerAdmin } from '../../settings/interfaces/ISite';
import { isProjectPlaceScope } from '../controllers/ProjectPlaceCtrl';
import { ISiteUserPlaceScope } from '../controllers/SiteUserPlaceCtrl';
import { FeatureFlags } from '../FeatureFlags';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { DatasourceContentType, ProjectContentType, ViewContentType, WorkbookContentType } from '../models/ContentTypes';
import { IDataConnectionInfo } from '../models/IDataConnectionInfo';
import { IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '../models/IDataSourceInfo';
import { IPlace } from '../models/IPlace';
import { IProjectBase, IProjectDetailInfo, IProjectInfo } from '../models/IProjectInfo';
import { IViewBase, IViewDetailInfo, IViewInfo } from '../models/IViewInfo';
import { IWorkbookBase, IWorkbookDetailInfo, IWorkbookInfo } from '../models/IWorkbookInfo';
import {
  IEditableItemInfo,
  IExtractItemInfo,
  INamedItemInfo,
  IOwnedItemInfo,
  ITaggedItemInfo,
} from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataConnections } from '../resources/DataConnections';
import { SiteAvailabilityState } from '../resources/Sites';
import {
  IDeleteAction as IDeleteActionApi,
  IDownloadAction,
  ILaunchWebAuthoringAction,
  IMoveAction,
  IRefreshExtractAction,
  ISetNameAction,
  ISetOwnerAction,
  ISetPermissionsAction,
  ISetProjectAction,
  ISetTagsAction,
} from '../ServerApi/types/actionNames';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { IResult } from '../ServerApi/types/IResponse';
import { IViewActions } from '../ServerApi/types/IViewActions';
import { IWorkbookActions } from '../ServerApi/types/IWorkbookActions';
import { ActionHelpers, ActionName } from './ActionHelpers';
import {
  ContentActions,
  IChangeOwnerAction,
  IChangeTagsAction,
  IDeleteAction,
  IEditSiteAction,
  IEditWorkbookAction,
  IUpdateSiteAvailabilityAction,
} from './ContentActions';
import { DefaultStateFor } from './ContentUrl';
import { CreateExtractTasksAction, CreateExtractTasksMode } from './CreateExtractTasksAction';
import { DeleteDatasourcesAction, IDeleteDatasourcesActionResult } from './DeleteDatasourcesAction';
import { ConnectionEditabilityHelper, EditConnectionAction } from './EditConnectionAction';
import { EditPermissionsAction } from './EditPermissionsAction';
import { HideViewsAction } from './HideViewsAction';
import { RevisionHistoryAction } from './RevisionHistoryAction';
import { ServerService } from './ServerService';
import { SetDisplayTabsAction } from './SetDisplayTabsAction';

// Action Interfaces

export interface IActionButton<TSummary, TDetail> {
  name: string;
  translate?: string;
  isAvailable(items: Set<TSummary>, detailedItem?: TDetail, allowedActions?: any): boolean;
  isDisabled?(items: Set<TSummary>, detailedItem?: TDetail, allowedActions?: any): boolean;
  execute(items: Set<TSummary>, context: IActionContext, detailedItem?: TDetail): void;
}

export interface IListActionButton {
  name: string;
  translate?: string;
  isAvailable(): boolean;
  execute($event: JQueryEventObject): void;
}

interface IExtractAndOwnedItem extends IExtractItemInfo, IOwnedItemInfo {}

/**
 * This class contains actions designed to be used in the actions menu.
 *
 * TODO: Rename this class. It's called ActionPanelService because before the actions menu,
 *       there was a panel where actions were executed.
 */
export class ActionPanelService {

  public static $inject = [
    '$state',
    '$q',
    'ServerService',
    'ContentActions',
    'DataConnections',
    'EditConnectionAction',
    'EditPermissionsAction',
    'HideViewsAction',
    'SetDisplayTabsAction',
    'CreateExtractTasksAction',
    'DeleteDatasourcesAction',
    'RevisionHistoryAction'
  ];

  private bind<T extends Function>(fn: T): T {
    return <T> angular.bind(this.contentActions, fn);
  }

  private ProjectActions: IActionButton<IProjectInfo, IProjectDetailInfo>[];
  private DatasourceActions: IActionButton<IDataSourceInfo, IDataSourceDetailInfo>[];
  private DataConnectionActions: IActionButton<IDataConnectionInfo, {}>[];
  private WorkbookActions: IActionButton<IWorkbookInfo, IWorkbookDetailInfo>[];
  private ViewActions: IActionButton<IViewInfo, IViewDetailInfo>[];
  private SitesActions: IActionButton<ISiteSettingsForServerAdmin, {}>[];

  constructor(
    private $state: ng.ui.IStateService,
    private $q: ng.IQService,
    private ServerService: ServerService,
    private contentActions: ContentActions,
    private dataConnections: DataConnections,
    private EditConnectionAction: EditConnectionAction,
    private EditPermissionsAction: EditPermissionsAction,
    private HideViewsAction: HideViewsAction,
    private SetDisplayTabsAction: SetDisplayTabsAction,
    private CreateExtractTasksAction: CreateExtractTasksAction,
    private DeleteDatasourcesAction: DeleteDatasourcesAction,
    private RevisionHistoryAction: RevisionHistoryAction
  ) {

    this.ProjectActions = _.compact([
      this.moveProjectsToProjectAction(),
      this.renameProjectAction(),
      this.permissionsAction(ProjectContentType.name),
      this.changeOwnerAction(this.bind<IChangeOwnerAction>(contentActions.changeOwnerOnProjects), CommonErrorCode.ProjectNotFound),
      this.deleteProjectsAction()
    ]);

    this.DatasourceActions = _.compact([
      this.newWorkbookAction(),
      FeatureFlags.metricsAuthoringPathEnabled ? this.newMetricAction() : null,
      this.downloadDatasourceAction(),
      this.setTagsOnDatasourcesAction(),
      this.permissionsAction(DatasourceContentType.name),
      this.moveDatasourcesToProjectAction(),
      this.changeOwnerOnDatasourcesAction(),
      this.createExtractRefreshForDatasourcesAction(),
      this.editConnectionForDatasourceAction(),
      this.viewDataSourceRevisionHistoryAction(),
      this.deleteDatasourcesAction()
    ]);

    this.DataConnectionActions = [
      this.editDataConnectionAction(),
    ];

    this.WorkbookActions = _.compact([
      this.editAction(this.bind<IEditWorkbookAction>(contentActions.editWorkbook), 'editWorkbook_action'),
      this.downloadWorkbookAction(),
      this.changeTags(this.bind<IChangeTagsAction>(contentActions.setTagsOnWorkbooks), CommonErrorCode.WorkbookNotFound),
      this.permissionsAction(WorkbookContentType.name),
      this.moveWorkbooksToProjectAction(),
      this.renameWorkbookAction(),
      this.changeOwnerAction(this.bind<IChangeOwnerAction>(contentActions.changeOwnerOnWorkbooks), CommonErrorCode.WorkbookNotFound),
      this.createExtractRefreshForWorkbooksAction(),
      this.setDisplayTabsAction(),
      this.viewWorkbookRevisionHistoryAction(),
      this.deleteWorkbooksAction()
    ]);

    this.ViewActions = _.compact([
      this.editAction(this.bind<IEditWorkbookAction>(contentActions.editView), 'editView_action'),
      this.changeTags(this.bind<IChangeTagsAction>(contentActions.setTagsOnViews), CommonErrorCode.ViewNotFound),
      FeatureFlags.renameViewsEnabled ? this.renameViewAction() : null,
      this.permissionsAction(ViewContentType.name),
      this.whoHasSeenThisViewAction(),
      this.deleteViewsAction()
    ]);

    this.SitesActions = [
      this.activateSiteAction(this.bind<IUpdateSiteAvailabilityAction>(contentActions.activateSite)),
      this.suspendSiteAction(this.bind<IUpdateSiteAvailabilityAction>(contentActions.suspendSite)),
      this.editSiteSettingsAction(this.bind<IEditSiteAction>(contentActions.editSite)),
      this.deleteSitesAction(this.bind<IDeleteAction>(contentActions.deleteSites))
    ];
  }

  public getProjectActions() {
    return this.ProjectActions;
  }

  public getDatasourceActions(place: IPlace): IActionButton<IDataSourceInfo, IDataSourceDetailInfo>[] {
    // Data sources embedded in a workbook don't have the normal actions because they are not first-class objects
    if (place.name === 'workbook') {
      return [ this.editConnectionForDatasourceAction() ];
    } else if (place.name === 'datasource') {
      // The edit connection action is removed from the datasource place page to prevent possible user
      // confusion between the action at the datasource level and the action at the connection level
      return _.filter(this.DatasourceActions, action => action.name !== 'edit-connection');
    } else {
      return this.DatasourceActions;
    }
  }

  public getDataConnectionActions() {
    return this.DataConnectionActions;
  }

  public getWorkbookActions() {
    return this.WorkbookActions;
  }

  public getViewActions() {
    return this.ViewActions;
  }

  public getSitesActions() {
    return this.SitesActions;
  }

  // Helper functions

  public static anySelected<T>(items: Set<T>): boolean {
    return items.size() > 0;
  }

  private static oneSelected<T>(items: Set<T>): boolean {
    return items.size() === 1;
  }

  private static isTaggingDisabled(allowedActions: ISetTagsAction): boolean {
    return !!allowedActions && !allowedActions.setTags;
  }

  private static oneSelectedEmbeddedDatasource(items: Set<IDataSourceBase>): boolean {
    return ActionPanelService.oneSelected(items) && !!items.first().workbook;
  }

  private oneSelectedUnrefreshableItem(items: Set<IExtractAndOwnedItem>): boolean {
    if (ActionPanelService.oneSelected(items)) {
      const item = items.first();
      return !item.hasExtracts;
    } else {
      return false;
    }
  }

  private oneSelectedUnrefreshableDatasource(datasources: Set<IDataSourceBase>): boolean {
    if (ActionPanelService.oneSelected(datasources)) {
      const datasource = datasources.first();
      return !this.CreateExtractTasksAction.isRefreshableDatasource(datasource);
    } else {
      return false;
    }
  }

  private getUpdateSiteAvailabilityDisabledFn(toAvailabilityStateName: string): (items: Set<ISiteSettingsForServerAdmin>)=>boolean {
      return (items: Set<ISiteSettingsForServerAdmin>) => {
        if (items.size() === 1) {
          // Check if default site or if the action will not change availability
          const item = items.first();
          return item.settings.urlName === ''
            || item.availability === toAvailabilityStateName
            || item.availability === SiteAvailabilityState.LOCKED;
        } else {
          return false;
        }
      };
  }

  // Action Button factory functions

  private moveProjectsToProjectAction(): IActionButton<IProjectBase, IProjectBase> {
    const actionName: ActionName = 'move';
    return {
      name: actionName,
      translate: 'move_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IProjectBase>, detailedItem?: IProjectBase, allowedActions?: IMoveAction) =>
        (!!allowedActions && !allowedActions.setProject),
      execute: (items: Set<IProjectBase>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        this.contentActions.moveProjectsToProject(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectItemsAfterAction(actionName, scope.place.name, ProjectContentType.name,
              items, result, CommonErrorCode.ProjectNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private moveDatasourcesToProjectAction(): IActionButton<IDataSourceBase, IDataSourceBase> {
    const actionName: ActionName = 'move';
    return {
      name: actionName,
      translate: 'move_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IDataSourceBase>, detailedItem?: IDataSourceBase, allowedActions?: ISetProjectAction) =>
        (!!allowedActions && !allowedActions.setProject) || ActionPanelService.oneSelectedEmbeddedDatasource(items),
      execute: (items: Set<IDataSourceBase>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        this.contentActions.moveDatasourcesToProject(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectItemsAfterAction(actionName, scope.place.name, DatasourceContentType.name,
              items, result, CommonErrorCode.DatasourceNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private moveWorkbooksToProjectAction(): IActionButton<IWorkbookBase, IWorkbookBase> {
    const actionName: ActionName = 'move';
    return {
      name: actionName,
      translate: 'move_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IWorkbookBase>, detailedItem?: IWorkbookBase, allowedActions?: ISetProjectAction) =>
        (!!allowedActions && !allowedActions.setProject),
      execute: (items: Set<IWorkbookBase>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        this.contentActions.moveWorkbooksToProject(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectItemsAfterAction(actionName, scope.place.name, WorkbookContentType.name,
              items, result, CommonErrorCode.WorkbookNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private deleteProjectsAction(): IActionButton<IProjectInfo, IProjectInfo> {
    return {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IProjectInfo>, detailedItem?: IProjectInfo, allowedActions?: IDeleteActionApi) =>
        (!!allowedActions && !allowedActions.del),
      execute: (items: Set<IProjectInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        this.contentActions.deleteProjects(items, context)
          .then((result: IResult) => {
            if (isProjectPlaceScope(scope) && scope.project && scope.project.id === items.first().id) {
              // deleted the project from its own place page
              if (items.first().topLevelProject) {
                this.$state.go('main.site.content.site.projects');
              } else {
                this.$state.go(DefaultStateFor(ProjectContentType.name), {id: items.first().parentId});
              }
            } else {
              ActionHelpers.deselectAllExceptErrors(items, result, CommonErrorCode.ProjectNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  private deleteDatasourcesAction(): IActionButton<IDataSourceBase, IDataSourceBase> {
    return {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IDataSourceBase>, detailedItem?: IDataSourceBase, allowedActions?: IDeleteActionApi) =>
        (!!allowedActions && !allowedActions.del) || ActionPanelService.oneSelectedEmbeddedDatasource(items),
      execute: (items: Set<IDataSourceBase>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        const projectId = items.first().project.id;
        this.DeleteDatasourcesAction.confirmDeleteDatasources(items, context)
          .then((result: IDeleteDatasourcesActionResult) => {
            if (!result.allowed) return;

            if (scope.place.name === 'datasource') {
              this.$state.go('main.site.content.project', {id: projectId});
            } else {
              ActionHelpers.deselectAllExceptErrors(
                items, result.serverResult, CommonErrorCode.DatasourceNotFound);
              scope.refreshContent();
            }

          });
      }
    };
  }

  private deleteWorkbooksAction(): IActionButton<IWorkbookInfo, IWorkbookInfo> {
    return {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IWorkbookInfo>, detailedItem?: IWorkbookInfo, allowedActions?: IDeleteActionApi) =>
        (!!allowedActions && !allowedActions.del),
      execute: (items: Set<IWorkbookInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        const projectId = items.first().project.id;
        this.contentActions.deleteWorkbooks(items, context)
          .then((result: IResult) => {
            if (scope.place.name === 'workbook') {
              this.$state.go(DefaultStateFor(ProjectContentType.name), { id: projectId });
            } else {
              ActionHelpers.deselectAllExceptErrors(items, result, CommonErrorCode.WorkbookNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  private deleteViewsAction(): IActionButton<IViewBase, IViewDetailInfo> {
    return {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (views: Set<IViewInfo>, detailedItem?: IViewDetailInfo, allowedActions?: IViewActions) => {
        return !!allowedActions && !allowedActions.del;
      },
      execute: (items: Set<IViewInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        this.HideViewsAction.confirmDeleteViews(items, context)
          .then((result: IResult) => {
            if (scope.place.name === 'workbook') {
              // If we're in a Workbook place (views subplace), we'll need to either redirect or refresh.
              const allViewsSelected = items.size() === scope.subplaceCounts['views'];
              if (allViewsSelected) {
                // All views were deleted, the subplace is empty, redirect to the Project place.
                this.$state.go(DefaultStateFor(ProjectContentType.name), {
                  id: (context.scope as any).workbook.project.id
                });
              } else {
                // Fewer than all views were deleted, reload to refresh the workbook's view-specific URLs.
                this.$state.hardReload();
              }
            } else {
              ActionHelpers.deselectAllExceptErrors(items, result, CommonErrorCode.ViewNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  private viewWorkbookRevisionHistoryAction(): IActionButton<IWorkbookBase, IWorkbookBase> {
    return {
      name: 'view-revision-history',
      translate: 'revisionHistory_action',
      isAvailable: (items: Set<IWorkbookInfo>) =>
        this.ServerService.isVersioningEnabled() && ActionPanelService.anySelected(items),
      isDisabled: (items: Set<IWorkbookBase>, detailedItem?: IWorkbookBase, allowedActions?: IWorkbookActions) =>
        (!!allowedActions && !allowedActions.versionManagement) || !ActionPanelService.oneSelected(items),
      execute: (items: Set<IWorkbookBase>) => {
        this.RevisionHistoryAction.showWorkbookRevisionHistory(items.first());
      }
    };
  }

  private viewDataSourceRevisionHistoryAction(): IActionButton<IDataSourceBase, IDataSourceBase> {
    return {
      name: 'view-revision-history',
      translate: 'revisionHistory_action',
      isAvailable: (items: Set<IDataSourceBase>) =>
        this.ServerService.isVersioningEnabled() && ActionPanelService.anySelected(items),
      isDisabled: (items: Set<IDataSourceBase>, detailedItem?: IDataSourceBase, allowedActions?: IDatasourceActions) =>
        (!!allowedActions && !allowedActions.versionManagement) || !ActionPanelService.oneSelected(items),
      execute: (items: Set<IDataSourceBase>) => {
        this.RevisionHistoryAction.showDataSourceRevisionHistory(items.first());
      }
    };
  }

  private renameProjectAction() : IActionButton<INamedItemInfo, INamedItemInfo> {
    return {
      name: 'rename',
      translate: 'rename_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<INamedItemInfo>, detailedItem?: INamedItemInfo, allowedActions?: ISetNameAction) =>
        (!!allowedActions && !allowedActions.setName) || !ActionPanelService.oneSelected(items),
      execute: (items: Set<INamedItemInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.contentActions.renameProjectPopup(items.first(), context)
          .then(() => scope.refreshContent());
      }
    };
  }

  private renameWorkbookAction() : IActionButton<INamedItemInfo, INamedItemInfo> {
    return {
      name: 'rename',
      translate: 'rename_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<INamedItemInfo>, detailedItem?: INamedItemInfo, allowedActions?: ISetNameAction) =>
        (!!allowedActions && !allowedActions.setName) || !ActionPanelService.oneSelected(items),
      execute: (items: Set<INamedItemInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.contentActions.renameWorkbookPopup(items.first(), context)
          .then(() => scope.refreshContent());
      }
    };
  }

  private renameViewAction() : IActionButton<INamedItemInfo, INamedItemInfo> {
    return {
      name: 'rename',
      translate: 'rename_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<INamedItemInfo>, detailedItem?: INamedItemInfo, allowedActions?: ILaunchWebAuthoringAction) =>
        (!!allowedActions && !allowedActions.launchWebAuthoring) || !ActionPanelService.oneSelected(items),
      execute: (items: Set<INamedItemInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.contentActions.renameViewPopup(items.first(), context)
          .then(() => scope.refreshContent());
      }
    };
  }

  private downloadDatasourceAction(): IActionButton<IDataSourceBase, IDataSourceBase> {
    return {
      name: 'download',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IDataSourceBase>, detailedItem?: IDataSourceBase, allowedActions?: IDownloadAction) =>
        (!!allowedActions && !allowedActions.download) || !ActionPanelService.oneSelected(items)
          || ActionPanelService.oneSelectedEmbeddedDatasource(items),
      execute: (items: Set<IDataSourceBase>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.contentActions.downloadDatasource(items.first())
          .catch(() => scope.refreshContent()); // May have failed this action was taken on stale information
      }
    };
  }

  private downloadWorkbookAction(): IActionButton<IWorkbookBase, IWorkbookBase> {
    return {
      name: 'download',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IWorkbookBase>, detailedItem?: IWorkbookBase, allowedActions?: IDownloadAction) =>
        (!!allowedActions && !allowedActions.download) || !ActionPanelService.oneSelected(items),
      execute: (items: Set<IWorkbookBase>, context: IActionContext, detailedItem?: IWorkbookBase) => {
        const workbook = detailedItem || items.first();
        const scope = <RefreshContentScope>context.scope;
        this.contentActions.downloadWorkbook(workbook)
          .catch(() => scope.refreshContent()); // May have failed this action was taken on stale information
      }
    };
  }

  private permissionsAction(contentType: string): IActionButton<any, any> {
    return {
      name: 'permissions',
      translate: 'permissions_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<any>, detailedItem?: any, allowedActions?: ISetPermissionsAction) => {
        const isOneEmbeddedDatasourceSelected = contentType === DatasourceContentType.name && ActionPanelService.oneSelectedEmbeddedDatasource(items);
        return (allowedActions && !allowedActions.setPermissions) || isOneEmbeddedDatasourceSelected;
      },
      execute: (items: Set<any>, context: IActionContext) => {
        // TFS 815536: Re-fetching the projects is necessary to avoid stale lock statuses being shown
        // in the permissions dialog. If we implement lock status via source authorizables
        // (TFS 667457), we can remove this re-fetch.
        let getItemsPromise: ng.IPromise<Set<any>> = this.$q.when(items);
        if (contentType === 'project') {
          getItemsPromise = getProjectInfos(items.toArray())
              .then(projectInfosArray => Set.fromIdItemArray(projectInfosArray));
        }

        getItemsPromise.then(
          itemsSet => this.EditPermissionsAction.showEditPermissionsDialog(itemsSet, contentType, context));
      }
    };
  }

  private changeOwnerAction(changeOwnerAction: IChangeOwnerAction, notFoundCode: number)
  : IActionButton<INamedItemInfo & IOwnedItemInfo, INamedItemInfo & IOwnedItemInfo> {
    const actionName: ActionName = 'change-owner';
    return {
      name: actionName,
      translate: 'changeOwner_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<INamedItemInfo & IOwnedItemInfo>, detailedItem?: INamedItemInfo & IOwnedItemInfo, allowedActions?: ISetOwnerAction) =>
        (ActionPanelService.oneSelected(items) && (!allowedActions || !allowedActions.setOwner)),
      execute: (items: Set<INamedItemInfo & IOwnedItemInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope & ISiteUserPlaceScope>context.scope;
        changeOwnerAction(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectItemsAfterAction(actionName, scope.place.name, '',
              items, result, notFoundCode);
            scope.refreshContent();
          });
      }
    };
  }

  private changeOwnerOnDatasourcesAction(): IActionButton<IDataSourceBase, IDataSourceBase> {
    const actionName: ActionName = 'change-owner';
    return {
      name: actionName,
      translate: 'changeOwner_action',
      isDisabled: (items: Set<IDataSourceBase>, detailedItem?: IDataSourceBase, allowedActions?: ISetOwnerAction) =>
          (ActionPanelService.oneSelected(items) && (!allowedActions || !allowedActions.setOwner))
              || ActionPanelService.oneSelectedEmbeddedDatasource(items),
      isAvailable: ActionPanelService.anySelected,
      execute: (items: Set<IDataSourceBase>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        this.contentActions.changeOwnerOnDatasources(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectItemsAfterAction(actionName, scope.place.name, DatasourceContentType.name,
              items, result, CommonErrorCode.DatasourceNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private suspendSiteAction(suspendSiteAction: IUpdateSiteAvailabilityAction): IActionButton<ISiteSettingsForServerAdmin, {}> {
    return {
      name: 'suspend-site',
      translate: 'suspend_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: this.getUpdateSiteAvailabilityDisabledFn(SiteAvailabilityState.SUSPENDED),
      execute: (items: Set<ISiteSettingsForServerAdmin>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        suspendSiteAction(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(items, result, CommonErrorCode.SiteNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private activateSiteAction(activateSiteAction: IUpdateSiteAvailabilityAction): IActionButton<ISiteSettingsForServerAdmin, {}> {
    return {
      name: 'activate-site',
      translate: 'activate_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: this.getUpdateSiteAvailabilityDisabledFn(SiteAvailabilityState.ACTIVE),
      execute: (items: Set<ISiteSettingsForServerAdmin>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        activateSiteAction(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(items, result, CommonErrorCode.SiteNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private editSiteSettingsAction(editSiteAction: IEditSiteAction): IActionButton<ISiteSettingsForServerAdmin, {}> {
    return {
      name: 'edit-site-settings',
      translate: 'editSiteSettings_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<ISiteSettingsForServerAdmin>) => !ActionPanelService.oneSelected(items),
      execute: (items: Set<ISiteSettingsForServerAdmin>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        editSiteAction(items.first(), context)
          .then(() => {
            this.ServerService.refreshSessionInfo();
            scope.refreshContent();
          });
      }
    };
  }

  private deleteSitesAction(deleteSitesAction: IDeleteAction): IActionButton<ISiteSettingsForServerAdmin, {}> {
    return {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<ISiteSettingsForServerAdmin>) => {
        return items.size() === 1 && items.first().settings.urlName === '';
      },
      execute: (items: Set<ISiteSettingsForServerAdmin>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        deleteSitesAction(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectAllExceptErrors(items, result, CommonErrorCode.SiteNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private editAction(editAction: IEditWorkbookAction, translate: string): IActionButton<any, IEditableItemInfo> {
    return {
      name: 'edit-workbook',
      translate: translate,
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<any>, detailedItem?: IEditableItemInfo, allowedActions?: ILaunchWebAuthoringAction) => {
        return !detailedItem
            || !detailedItem.editUrl
            || (!!allowedActions && !allowedActions.launchWebAuthoring);
      },
      execute: (items: Set<any>, context: IActionContext, detailedItem?: IEditableItemInfo) => {
        detailedItem && editAction(detailedItem);
      }
    };
  }

  private newWorkbookAction(): IActionButton<IDataSourceBase, IDataSourceDetailInfo> {
    return {
      name: 'new-workbook',
      translate: 'newWorkbook',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IDataSourceBase>, detailedItem?: IDataSourceDetailInfo, allowedActions?: IDatasourceActions) => {
        return !detailedItem
          || !detailedItem.urlName
          || (!!allowedActions && !allowedActions.launchWebAuthoring);
      },
      execute: (items: Set<any>, context: IActionContext, detailedItem?: IDataSourceDetailInfo) => {
        detailedItem && this.contentActions.newWorkbookFromDatasource(detailedItem, this.ServerService.sessionSiteUrlName());
      }
    };
  }

  private newMetricAction(): IActionButton<IDataSourceBase, IDataSourceDetailInfo> {
    return {
      name: 'new-metric',
      translate: 'newMetric',
      isAvailable: (items: Set<IDataSourceBase>) => ActionPanelService.anySelected(items) && FeatureFlags.metricsAuthoringPathEnabled,
      isDisabled: (items: Set<IDataSourceBase>, detailedItem?: IDataSourceDetailInfo, allowedActions?: IDatasourceActions) => {
        return !detailedItem || !detailedItem.hasExtracts || items.size() !== 1;
      },
      execute: (items: Set<any>, context: IActionContext, detailedItem?: IDataSourceDetailInfo) => {
        items.first() && this.contentActions.newMetric(items.first(), this.ServerService.sessionSiteUrlName());
      }
    };
  }

  private changeTags(changeTags: IChangeTagsAction, notFoundCode: number): IActionButton<ITaggedItemInfo, {}> {
    return {
      name: 'change-tags',
      translate: 'tag_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<ITaggedItemInfo>, detailedItem?: {}, allowedActions?: ISetTagsAction) => {
        return ActionPanelService.isTaggingDisabled(allowedActions);
      },
      execute: (items: Set<ITaggedItemInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        changeTags(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(items, result, notFoundCode);
            scope.refreshContent();
          });
      }
    };
  }

  private setTagsOnDatasourcesAction(): IActionButton<IDataSourceBase, IDataSourceBase> {
    return {
      name: 'change-tags',
      translate: 'tag_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IDataSourceBase>, detailedItem?: IDataSourceDetailInfo, allowedActions?: ISetTagsAction) => {
        return ActionPanelService.oneSelectedEmbeddedDatasource(items) || ActionPanelService.isTaggingDisabled(allowedActions);
      },
      execute: (items: Set<IDataSourceBase>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.contentActions.setTagsOnDatasources(items, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(items, result, CommonErrorCode.DatasourceNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private createExtractRefreshForDatasourcesAction(): IActionButton<IDataSourceBase, IDataSourceDetailInfo> {
    return {
      name: 'create-extract-refresh',
      translate: 'extractTasks_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (datasources: Set<IDataSourceBase>, detailedItem?: IDataSourceDetailInfo, allowedActions?: IRefreshExtractAction) => {
        return (!!allowedActions && !allowedActions.refreshExtract)
            || this.oneSelectedUnrefreshableDatasource(datasources)
            || (!FeatureFlags.dataSyncRefreshNowEnabled && !!detailedItem && detailedItem.refreshMode === RefreshModeTypes.Remote);
      },
      execute: (datasources: Set<IDataSourceBase>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.CreateExtractTasksAction.forDatasources(datasources, context, CreateExtractTasksMode.AllowBoth)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(datasources, result, CommonErrorCode.DatasourceNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private createExtractRefreshForWorkbooksAction(): IActionButton<IWorkbookBase, IWorkbookBase> {
    return {
      name: 'create-extract-refresh',
      translate: 'extractTasks_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (items: Set<IWorkbookBase>, detailedItem?: IWorkbookBase, allowedActions?: IRefreshExtractAction) => {
        return (!!allowedActions && !allowedActions.refreshExtract) || this.oneSelectedUnrefreshableItem(items);
      },
      execute: (workbooks: Set<IWorkbookBase>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.CreateExtractTasksAction.forWorkbooks(workbooks, context, CreateExtractTasksMode.AllowBoth)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(workbooks, result, CommonErrorCode.WorkbookNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private editDataConnectionAction(): IActionButton<IDataConnectionInfo, {}> {
    return {
      name: 'edit-connection',
      translate: 'editConnection_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (dataConnections: Set<IDataConnectionInfo>) =>
        dataConnections.size() === 1 &&
          !ConnectionEditabilityHelper.userCanEditConnection(dataConnections.first(), appStore.getState()),
      execute: (dataConnections: Set<IDataConnectionInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.EditConnectionAction.confirmEditConnections(dataConnections.toArray(), context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(dataConnections, result, CommonErrorCode.DataConnectionNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private editConnectionForDatasourceAction(): IActionButton<IDataSourceBase, IDataSourceBase> {

    function getDatasourceIdsFromConnections(connections: IDataConnectionInfo[]): Set<string> {
      const datasourceIdSet = new Set(Set.Identity);
      for (const connection of connections) {
        const datasource = connection.datasource;
        if (datasource) {
          datasourceIdSet.add(datasource.id);
        }
      }
      return datasourceIdSet;
    }

    function editingDisabled(dataSources: Set<IDataSourceBase>) {
      // Eagerly try to edit if multiple connections are selected; the EditConnectionAction will
      // display a dialog if not all the connections can be edited together.
      return dataSources.size() === 1 && dataSources.first().dataConnectionsCount === 1
          && !ConnectionEditabilityHelper.userCanEditDatasource(dataSources.first(), appStore.getState());
    }

    return {
      name: 'edit-connection',
      translate: 'editConnection_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: editingDisabled.bind(this),
      execute: (dataSources: Set<IDataSourceBase>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.dataConnections.getFromDatasources(dataSources)
          .then((connections: IDataConnectionInfo[]) => {
            // If any data sources have been removed from the server, deselect them on the client
            dataSources.removeItemsNotIn(getDatasourceIdsFromConnections(connections));
            return this.EditConnectionAction.confirmEditConnections(connections, context);
          })
          .then(() => scope.refreshContent());
      }
    };
  }

  private setDisplayTabsAction(): IActionButton<IWorkbookBase, IWorkbookBase> {
    return {
      name: 'setDisplayTabs',
      translate: 'setDisplayTabs_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (workbooks: Set<IWorkbookBase>, detail?: IWorkbookBase, allowedActions?: IWorkbookActions) => {
        return !!allowedActions && !allowedActions.setDisplayTabs;
      },
      execute: (workbooks: Set<IWorkbookBase>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.SetDisplayTabsAction.confirmSetDisplayTabs(workbooks, context)
          .then((result: IResult) => {
            ActionHelpers.deselectNotFound(workbooks, result, CommonErrorCode.WorkbookNotFound);
            scope.refreshContent();
          });
      }
    };
  }

  private whoHasSeenThisViewAction(): IActionButton<IViewInfo, IViewInfo> {
    return {
      name: 'whoHasSeenThisView',
      isAvailable: (items: Set<IViewInfo>) => ActionPanelService.anySelected(items),
      isDisabled: (views: Set<IViewInfo>) => {
        const owner = views.first().workbook.owner;
        const isAdminOrOwner = CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(owner)(appStore.getState());
        return views.size() !== 1 || !isAdminOrOwner;
      },
      execute: (views: Set<IViewInfo>) => {
        const viewId = views.first().id;
        this.contentActions.openWhoHasSeenThisView(viewId);
      }
    };
  }

}

Module.instance().service('ActionPanelService', ActionPanelService);
